import { useTranslation } from 'react-i18next';

import { BrandingPanel } from './BrandingPanel';
import { buildBoardTheme, type BoardTheme } from './boardTheme';
import { DisplayWaitingList } from './DisplayWaitingList';
import type { QmsDisplayPublic } from './types';
import { useBlinkWindow } from './useBlinkWindow';
import type { DisplayCallEntry, DisplayLiveState } from './useDisplayLive';
import { useFullscreen } from './useFullscreen';

/** Group / all-counters board: a live call list beside the branding panel.
 *  There is no text header — the uploaded logo carries the branding — and
 *  the whole board is themed from the layout's single `accent_color`. */
export function DisplayGroupView({
  board,
  live,
}: {
  board: QmsDisplayPublic;
  live: DisplayLiveState;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const layout = board.layout ?? { kind: 'group' };
  const theme = buildBoardTheme(
    layout.accent_color,
    layout.label_color,
    layout.label_size,
  );
  const noCounters = board.counters.length === 0;
  const { isFullscreen, toggle } = useFullscreen();

  return (
    <div
      className="relative flex h-screen flex-col overflow-hidden"
      data-testid="display-public-canvas"
      data-kind="group"
      style={{ background: theme.background, color: theme.foreground }}
    >
      <button
        type="button"
        onClick={toggle}
        className="absolute end-4 top-4 z-10 rounded border px-3 py-1 text-xs uppercase tracking-wider"
        style={{ borderColor: theme.border, color: theme.muted }}
        data-testid="display-fullscreen"
      >
        {t(
          isFullscreen
            ? 'displays.public.exit_fullscreen'
            : 'displays.public.fullscreen',
        )}
      </button>

      <main
        className="grid min-h-0 flex-1 gap-6 p-6"
        style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)' }}
        data-testid="display-group-main"
      >
        <div className="flex min-h-0 flex-col gap-6 overflow-y-auto">
          {layout.logo_url ? (
            <div className="shrink-0" data-testid="display-logo">
              {/* The logo leads the call list, on a white panel, and
                  fills the column width since the artwork carries text. */}
              <img
                src={layout.logo_url}
                alt=""
                aria-hidden="true"
                className="h-auto max-h-48 w-full rounded-xl bg-white object-contain p-3 shadow-sm"
              />
            </div>
          ) : null}
          <CallList calls={live.calls} theme={theme} noCounters={noCounters} />
          <DisplayWaitingList waiting={board.waiting} theme={theme} />
        </div>
        <BrandingPanel
          mediaKind={board.media_kind}
          mediaUrl={board.media_url}
          theme={theme}
        />
      </main>
    </div>
  );
}

// Public TV display, not an admin grid — DataTable's sorting/selection is the
// wrong primitive here, so this is a deliberate hand-rolled table.
function CallList({
  calls,
  theme,
  noCounters,
}: {
  calls: DisplayCallEntry[];
  theme: BoardTheme;
  noCounters: boolean;
}): JSX.Element {
  const { t } = useTranslation('qms');
  if (calls.length === 0) {
    return (
      <p
        className="m-auto text-2xl"
        style={{ color: theme.muted }}
        data-testid="display-empty-counters"
      >
        {t(
          noCounters
            ? 'displays.public.empty_counters'
            : 'displays.public.empty_calls',
        )}
      </p>
    );
  }
  return (
    <table
      className="w-full border-separate text-2xl"
      style={{ borderSpacing: '0 8px' }}
      data-testid="display-call-table"
    >
      <thead>
        <tr
          className="text-start font-semibold uppercase tracking-[0.3em]"
          style={{ color: theme.labelAccent, fontSize: theme.labelFontSize }}
        >
          <th scope="col" className="pb-1 ps-2">
            {t('displays.public.token')}
          </th>
          <th scope="col" className="pb-1 pe-2 text-end">
            {t('displays.public.counter')}
          </th>
        </tr>
      </thead>
      <tbody>
        {calls.map((call, index) => (
          <CallRow
            key={call.id}
            call={call}
            theme={theme}
            highlight={index === 0}
          />
        ))}
      </tbody>
    </table>
  );
}

function CallRow({
  call,
  theme,
  highlight,
}: {
  call: DisplayCallEntry;
  theme: BoardTheme;
  highlight: boolean;
}): JSX.Element {
  const blinking = useBlinkWindow(call.changedAt || undefined);
  return (
    <tr
      className={`transition-colors ${blinking ? 'qms-pulse' : ''}`}
      data-testid="display-call-row"
      data-counter-id={call.counterId}
      data-display-code={call.displayCode}
      style={{
        backgroundColor: highlight ? theme.highlightBackground : theme.surface,
        color: highlight ? theme.highlightForeground : theme.accent,
      }}
    >
      <td className="rounded-s-lg py-3 ps-3 font-mono font-black tabular-nums leading-none">
        {call.displayCode}
      </td>
      <td className="rounded-e-lg py-3 pe-3 text-end font-mono font-bold tabular-nums leading-none">
        {call.counterCode}
      </td>
    </tr>
  );
}
